import { useState, useCallback } from 'react';
import { SupplierDetail } from '../types/supplier.types';
import { loadFilters, saveFilters } from '@/shared/utils/filterStorage';
import { FILTER_STORAGE_KEY, SupplierFilters, filterSuppliers } from '../utils/supplierSelectionHelpers';

interface UseSupplierFilterParams {
    allSuppliers: SupplierDetail[];
    setSuppliers: React.Dispatch<React.SetStateAction<SupplierDetail[]>>;
    resetPagination: () => void;
}

interface UseSupplierFilterReturn {
    filters: SupplierFilters;
    setFilters: React.Dispatch<React.SetStateAction<SupplierFilters>>;
    handleSearch: () => void;
    handleQuickFilter: (newFilters: SupplierFilters) => void;
    handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const useSupplierFilter = ({
                                      allSuppliers,
                                      setSuppliers,
                                      resetPagination,
                                  }: UseSupplierFilterParams): UseSupplierFilterReturn => {
    const [filters, setFilters] = useState<SupplierFilters>(() => {
        const saved = loadFilters<SupplierFilters>(FILTER_STORAGE_KEY);
        return saved || { supplierName: '', supplierId: '', operationStatus: '전체' };
    });

    const handleSearch = useCallback(() => {
        setSuppliers(filterSuppliers(allSuppliers, filters));
        resetPagination();
        saveFilters(FILTER_STORAGE_KEY, filters);
    }, [allSuppliers, filters, setSuppliers, resetPagination]);

    const handleQuickFilter = useCallback(
        (newFilters: SupplierFilters) => {
            setFilters(newFilters);
            setSuppliers(filterSuppliers(allSuppliers, newFilters));
            resetPagination();
            saveFilters(FILTER_STORAGE_KEY, newFilters);
        },
        [allSuppliers, setSuppliers, resetPagination]
    );

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') handleSearch();
        },
        [handleSearch]
    );

    return { filters, setFilters, handleSearch, handleQuickFilter, handleKeyDown };
};
